import React, { FormEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavBar } from "@components/navigation/NavBar/NavBar";
import SubmitBtn from "@components/buttons/FormButtons/SubmitBtn";
import useServiceHook from '@hooks/useServiceHook';
import { PlanService, IPlan } from '@services/SubscriptionPlans';
import { Country } from "@utils/constants/localisation";
import LoadingSpinner from "@utils/loadingSpinner";
import { OnboardingAgreementState, OnboardingAgreementAction } from './onboardingAgreementReducer';

interface FlexiOwnNSWEVAddendumV2Props {
  navigateTarget: string;
  onboardingAgreementState: OnboardingAgreementState;
  onboardingAgreementDispatch: React.Dispatch<OnboardingAgreementAction>;
  custId: string;
}

export default function FlexiOwnNSWEVAddendumV2({
  navigateTarget,
  onboardingAgreementState,
  onboardingAgreementDispatch,
  custId,
}: FlexiOwnNSWEVAddendumV2Props) {
  const navigate = useNavigate();

  const [loading, error, errMsg, planData] = useServiceHook<IPlan>(
    PlanService.getPlan,
    [onboardingAgreementState.planId, Country.AU]
  );

  // Store NSW incentive data in agreement state so it reaches InvoicePreview and then the BE
  useEffect(() => {
    if (!loading && planData) {
      onboardingAgreementDispatch({
        type: "SET_ALL",
        payload: {
          nswTotalIncentiveExclGST: planData.NSWTotalIncentiveExclGST || "",
          nswWeeklyIncentiveExclGST: planData.NSWWeeklyIncentiveExclGST || "",
        }
      });
    }
  }, [loading, planData]);

  // Weekly subscription total already includes the NSW Weekly Incentive, so we need to add
  // back the NSW incentive to show the weekly subscription fee without the incentive for comparison
  const weeklySubsTotalWithIncentive: number = parseFloat(
    onboardingAgreementState.quoteAddons
      .filter((addon: any) => addon.included === true)
      .reduce((sum: number, item: any) => sum + (item.price_excluding_gst || 0), 0)
      .toFixed(2)
  );

  const nswWeeklyIncentive = planData ? Number(planData.NSWWeeklyIncentiveExclGST || 0) : 0;
  const weeklySubsTotalWithoutIncentive = weeklySubsTotalWithIncentive + nswWeeklyIncentive;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    navigate(navigateTarget);
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
  };

  const itemStyle: React.CSSProperties = {
    flex: 1,
    padding: '10px',
    boxSizing: 'border-box',
  };

  const textStyle: React.CSSProperties = {
    ...itemStyle,
    fontWeight: 'bold',
  };

  const numberStyle: React.CSSProperties = {
    ...itemStyle,
    textAlign: 'right',
  };

  // If no NSW incentive data, auto-navigate to next step
  if (!loading && planData && !planData.NSWWeeklyIncentiveExclGST) {
    navigate(navigateTarget);
    return null;
  }

  return (
    <>
      <NavBar pageTitle={"NSW EV Addendum"} backAction={undefined} />
      <form style={{ margin: '2.5vh 2.5vw' }} onSubmit={onSubmit}>
        {loading && <LoadingSpinner testId={"loading-spinner"} />}

        {!loading && planData && planData.NSWWeeklyIncentiveExclGST && (
          <>
            <p style={{ margin: '2.5vh 0' }}>
              NSW EV Subscription Only: As a successful recipient of the 'NSW EV Fleet Incentive',
              Splend is able to offer a discounted rate on the Weekly Vehicle Payment for the Vehicle
              in this Agreement ("NSW Splend EV Discount").
            </p>
            <div style={{ margin: '2.5vh 0' }}>
              <div style={rowStyle}>
                <div style={textStyle}>Weekly subscription without incentive ($)</div>
                <div style={numberStyle}>{weeklySubsTotalWithoutIncentive}</div>
              </div>

              <div style={rowStyle}>
                <div style={textStyle}>Total NSW government EV Fleet incentive ($)</div>
                <div style={numberStyle}>{planData.NSWTotalIncentiveExclGST}</div>
              </div>

              <div style={rowStyle}>
                <div style={textStyle}>Weekly subscription with incentive but excluding further discounts ($)</div>
                <div style={numberStyle}>{weeklySubsTotalWithIncentive}</div>
              </div>
            </div>
            <SubmitBtn text={"Continue"} />
          </>
        )}

        {!loading && error && (
          <div style={{ margin: '2.5vh 0', padding: '20px', backgroundColor: '#fee', border: '1px solid #fcc', borderRadius: '8px' }}>
            <p style={{ color: '#c00', fontWeight: 'bold' }}>Error loading NSW EV data</p>
            <p>{errMsg}</p>
          </div>
        )}
      </form>
    </>
  );
}